// components/DataTablesSection.tsx
import React, { useState } from 'react';
import {ServiceLogEntry, BuildingSummary, ServiceType} from '../types/analysis.types';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import './DataTablesSection.css'

interface DataTablesSectionProps {
    serviceTypes: ServiceType[];
    individualVisits: ServiceLogEntry[];
    logEntries: ServiceLogEntry[];
    commentEntries: ServiceLogEntry[];
    buildings: BuildingSummary[];
    users: any[];
    serviceTitles: Record<string, string>;
    selectedUserGroup: string | null;
    getSelectedGroupName: () => string;
}

const DataTablesSection: React.FC<DataTablesSectionProps> = ({
                                                                 serviceTypes,
                                                                 individualVisits,
                                                                 logEntries,
                                                                 commentEntries,
                                                                 buildings,
                                                                 users,
                                                                 serviceTitles,
                                                                 selectedUserGroup,
                                                                 getSelectedGroupName
                                                             }) => {
    const [isIndividualPostsCollapsed, setIsIndividualPostsCollapsed] = useState<boolean>(true);
    const [isIndividualVisitsCollapsed, setIsIndividualVisitsCollapsed] = useState<boolean>(true);
    const [isCommentsCollapsed, setIsCommentsCollapsed] = useState<boolean>(true);

    const toggleIndividualPostsCollapse = (): void => {
        setIsIndividualPostsCollapsed(!isIndividualPostsCollapsed);
    };

    const toggleIndividualVisitsCollapse = (): void => {
        setIsIndividualVisitsCollapsed(!isIndividualVisitsCollapsed);
    };

    const toggleCommentsCollapse = (): void => {
        setIsCommentsCollapsed(!isCommentsCollapsed);
    };

    return (
        <>
            <br/>
            {/* Individual Comments Section */}
            <div className="analysis-card">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                    onClick={toggleCommentsCollapse}
                >
                    <h5 className="entries-title" style={{ margin: 0, marginRight: '10px' }}>
                        Individuella kommentarer ({commentEntries.length})
                        {` för ${getSelectedGroupName()}`}
                    </h5>
                    <span style={{
                        fontSize: '1.2em',
                        fontWeight: 'bold',
                        transform: isCommentsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        display: 'inline-block'
                    }}>
            ▼
          </span>
                </div>

                {!isCommentsCollapsed && (
                    <>
                        {commentEntries.length > 0 ? (
                            <div className="entries-table-container">
                                <table className="entries-table">
                                    <thead>
                                    <tr>
                                        <th>Datum & Tid</th>
                                        <th>Byggnad</th>
                                        <th>Användare</th>
                                        <th>Kommentar</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {commentEntries.map((entry) => (
                                        <tr key={`comment-${entry.id}`}>
                                            <td>{formatDateTime(entry.dateString)}</td>
                                            <td>
                                                {buildings && buildings.find(building => building.key === entry.buildingKey) ? (
                                                    <a href={`/building/${entry.buildingKey}`} className="building-link">
                                                        {buildings.find(building => building.key === entry.buildingKey)?.name || entry.buildingKey}
                                                    </a>
                                                ) : (
                                                    entry.buildingKey
                                                )}
                                            </td>
                                            <td>{users ? (users.find(user => user.key === entry.user)?.name || entry.user) : entry.user}</td>
                                            <td>{entry.comment}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p>Inga kommentarer hittades för valt datumintervall{selectedUserGroup ? ` för ${getSelectedGroupName()}` : ''}</p>
                        )}
                    </>
                )}
            </div>

            <br/>
            {/* Individual Visits Section */}
            <div className="analysis-card">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                    onClick={toggleIndividualVisitsCollapse}
                >
                    <h5 className="entries-title" style={{ margin: 0, marginRight: '10px' }}>
                        Individuella besök ({individualVisits.length})
                        {` för ${getSelectedGroupName()}`}
                    </h5>
                    <span style={{
                        fontSize: '1.2em',
                        fontWeight: 'bold',
                        transform: isIndividualVisitsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        display: 'inline-block'
                    }}>
            ▼
          </span>
                </div>

                {!isIndividualVisitsCollapsed && (
                    <>
                        {individualVisits.length > 0 ? (
                            <div className="entries-table-container">
                                <div style={{
                                    marginBottom: '15px',
                                    padding: '10px',
                                    backgroundColor: '#f0f8ff',
                                    borderRadius: '4px',
                                    fontSize: '0.9em',
                                    color: '#333'
                                }}>
                                    <strong>Information:</strong> Denna tabell visar unika byggnadsbesök (ett per byggnad per dag, baserat på senaste besöket).
                                    För att se alla individuella tjänsteposter, expandera sektionen "Individuella poster" nedan.
                                </div>
                                <table className="entries-table">
                                    <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th>Byggnad</th>
                                        <th>Användare</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {individualVisits.map((visit) => (
                                        <tr key={`visit-${visit.id}`}>
                                            <td>{formatDate(visit.dateString)}</td>
                                            <td>
                                                {buildings && buildings.find(building => building.key === visit.buildingKey) ? (
                                                    <a href={`/building/${visit.buildingKey}`} className="building-link">
                                                        {buildings.find(building => building.key === visit.buildingKey)?.name || visit.buildingKey}
                                                    </a>
                                                ) : (
                                                    visit.buildingKey
                                                )}
                                            </td>
                                            <td>{users ? (users.find(user => user.key === visit.user)?.name || visit.user) : visit.user}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p>Inga besök hittades för valt datumintervall{selectedUserGroup ? ` för ${getSelectedGroupName()}` : ''}</p>
                        )}
                    </>
                )}
            </div>

            <br/>

            {/* Individual Posts Section */}
            <div className="analysis-card">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                    onClick={toggleIndividualPostsCollapse}
                >
                    <h5 className="entries-title" style={{ margin: 0, marginRight: '10px' }}>
                        Individuella poster ({logEntries.length})
                        {` för ${getSelectedGroupName()}`}
                    </h5>
                    <span style={{
                        fontSize: '1.2em',
                        fontWeight: 'bold',
                        transform: isIndividualPostsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        display: 'inline-block'
                    }}>
            ▼
          </span>
                </div>

                {!isIndividualPostsCollapsed && (
                    <>
                        {logEntries.length > 0 ? (
                            <div className="entries-table-container">
                                <table className="entries-table" >
                                    <thead>
                                    <tr>
                                        <th>Datum & Tid</th>
                                        <th>Byggnad</th>
                                        <th>Användare</th>
                                        <th>Tjänst</th>
                                        <th>Mängd</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {logEntries.map((entry) => (
                                        <tr key={entry.id}>
                                            <td>{formatDateTime(entry.dateString)}</td>
                                            <td>
                                                {buildings && buildings.find(building => building.key === entry.buildingKey) ? (
                                                    <a href={`/building/${entry.buildingKey}`} className="building-link">
                                                        {buildings.find(building => building.key === entry.buildingKey)?.name || entry.buildingKey}
                                                    </a>
                                                ) : (
                                                    entry.buildingKey
                                                )}
                                            </td>
                                            <td>{users ? (users.find(user => user.key === entry.user)?.name || entry.user) : entry.user}</td>
                                            <td>{serviceTitles[entry.serviceType || ''] || entry.serviceType || 'Okänd'}</td>
                                            <td>
                                                {entry.value !== undefined && (() => {
                                                    const unit = serviceTypes.find(st => st.key === entry.serviceType)?.unit;
                                                    return (
                                                        <>
                                                            {entry.value}
                                                            {unit ? ` ${unit}` : ""}
                                                        </>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p>Inga poster hittades för valt datumintervall{selectedUserGroup ? ` för ${getSelectedGroupName()}` : ''}</p>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default DataTablesSection;